import { FC } from 'react';
import { useHealthAndSafetyState } from '../../contexts/healthAndSafety.context';
import { Grid, Chip, Box, Divider } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  THealthIncident,
  TSafetyProtocol,
  TEmergencyContact,
  THealthCheckup,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface HealthAndSafetyGridProps {
  viewType: 'incidents' | 'protocols' | 'contacts' | 'checkups';
  onRowClick: (
    data: THealthIncident | TSafetyProtocol | TEmergencyContact | THealthCheckup
  ) => void;
  isLoading?: boolean;
}

const HealthAndSafetyGrid: FC<HealthAndSafetyGridProps> = ({
  viewType,
  onRowClick,
  isLoading = false,
}) => {
  const { translateText } = useTranslation();
  const { incidents, protocols, emergencyContacts, healthCheckups } =
    useHealthAndSafetyState();

  const getIncidentsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'incidentType',
        headerName: translateText(
          'administration.healthAndSafety.columns.type'
        ),
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.row.incidentType}
            color={
              params.row.incidentType === 'INJURY'
                ? 'error'
                : params.row.incidentType === 'ALLERGY'
                  ? 'warning'
                  : params.row.incidentType === 'ILLNESS'
                    ? 'info'
                    : params.row.incidentType === 'ACCIDENT'
                      ? 'secondary'
                      : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'severity',
        headerName: translateText(
          'administration.healthAndSafety.columns.severity'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.severity}
            color={
              params.row.severity === 'HIGH'
                ? 'error'
                : params.row.severity === 'MEDIUM'
                  ? 'warning'
                  : params.row.severity === 'LOW'
                    ? 'success'
                    : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'location',
        headerName: translateText(
          'administration.healthAndSafety.columns.location'
        ),
        width: 150,
        renderCell: (params) => params.row.location,
      },
      {
        field: 'affectedPersonType',
        headerName: translateText(
          'administration.healthAndSafety.columns.personType'
        ),
        width: 120,
        renderCell: (params) => params.row.affectedPersonType,
      },
      {
        field: 'incidentDate',
        headerName: translateText(
          'administration.healthAndSafety.columns.date'
        ),
        width: 120,
        renderCell: (params) => params.row.incidentDate?.toLocaleDateString(),
      },
      {
        field: 'status',
        headerName: translateText(
          'administration.healthAndSafety.columns.status'
        ),
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.row.status}
            color={
              params.row.status === 'RESOLVED'
                ? 'success'
                : params.row.status === 'IN_PROGRESS'
                  ? 'warning'
                  : params.row.status === 'CLOSED'
                    ? 'info'
                    : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'firstAidProvided',
        headerName: translateText(
          'administration.healthAndSafety.columns.firstAid'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.firstAidProvided
                ? translateText('administration.healthAndSafety.values.yes')
                : translateText('administration.healthAndSafety.values.no')
            }
            color={params.row.firstAidProvided ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'medicalAttentionRequired',
        headerName: translateText(
          'administration.healthAndSafety.columns.medicalCare'
        ),
        width: 120,
        renderCell: (params) => (
          <Chip
            label={
              params.row.medicalAttentionRequired
                ? translateText(
                  'administration.healthAndSafety.values.required'
                )
                : translateText(
                  'administration.healthAndSafety.values.notRequired'
                )
            }
            color={params.row.medicalAttentionRequired ? 'error' : 'success'}
            size="small"
          />
        ),
      },
      {
        field: 'reportedBy',
        headerName: translateText(
          'administration.healthAndSafety.columns.reportedBy'
        ),
        width: 120,
        renderCell: (params) => params.row.reportedBy,
      },
    ];
  };

  const getProtocolsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'protocolName',
        headerName: translateText(
          'administration.healthAndSafety.columns.protocolName'
        ),
        width: 200,
        renderCell: (params) => params.row.protocolName,
      },
      {
        field: 'category',
        headerName: translateText(
          'administration.healthAndSafety.columns.category'
        ),
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.category.replace(/_/g, ' ')}
            color="primary"
            size="small"
          />
        ),
      },
      {
        field: 'description',
        headerName: translateText(
          'administration.healthAndSafety.columns.description'
        ),
        width: 250,
        renderCell: (params) => params.row.description,
      },
      {
        field: 'responsiblePerson',
        headerName: translateText(
          'administration.healthAndSafety.columns.responsible'
        ),
        width: 120,
        renderCell: (params) => params.row.responsiblePerson,
      },
      {
        field: 'isActive',
        headerName: translateText(
          'administration.healthAndSafety.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText('administration.healthAndSafety.status.active')
                : translateText(
                  'administration.healthAndSafety.status.inactive'
                )
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'trainingRequired',
        headerName: translateText(
          'administration.healthAndSafety.columns.training'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.trainingRequired
                ? translateText(
                  'administration.healthAndSafety.values.required'
                )
                : translateText(
                  'administration.healthAndSafety.values.notRequired'
                )
            }
            color={params.row.trainingRequired ? 'warning' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'lastUpdated',
        headerName: translateText(
          'administration.healthAndSafety.columns.lastUpdated'
        ),
        width: 130,
        renderCell: (params) => params.row.lastUpdated?.toLocaleDateString(),
      },
      {
        field: 'nextReviewDate',
        headerName: translateText(
          'administration.healthAndSafety.columns.nextReview'
        ),
        width: 130,
        renderCell: (params) => params.row.nextReviewDate?.toLocaleDateString(),
      },
    ];
  };

  const getContactsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'name',
        headerName: translateText(
          'administration.healthAndSafety.columns.contactName'
        ),
        width: 200,
        renderCell: (params) => params.row.name,
      },
      {
        field: 'relationship',
        headerName: translateText(
          'administration.healthAndSafety.columns.relationship'
        ),
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.relationship.replace(/_/g, ' ')}
            color="primary"
            size="small"
          />
        ),
      },
      {
        field: 'phoneNumber',
        headerName: translateText(
          'administration.healthAndSafety.columns.phone'
        ),
        width: 150,
        renderCell: (params) => params.row.phoneNumber,
      },
      {
        field: 'email',
        headerName: translateText(
          'administration.healthAndSafety.columns.email'
        ),
        width: 200,
        renderCell: (params) =>
          params.row.email ||
          translateText('administration.healthAndSafety.values.nA'),
      },
      {
        field: 'priority',
        headerName: translateText(
          'administration.healthAndSafety.columns.priority'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={`P${params.row.priority}`}
            color={
              params.row.priority === 1
                ? 'error'
                : params.row.priority === 2
                  ? 'warning'
                  : params.row.priority === 3
                    ? 'info'
                    : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'isActive',
        headerName: translateText(
          'administration.healthAndSafety.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText('administration.healthAndSafety.status.active')
                : translateText(
                  'administration.healthAndSafety.status.inactive'
                )
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'personType',
        headerName: translateText(
          'administration.healthAndSafety.columns.personType'
        ),
        width: 120,
        renderCell: (params) => params.row.personType,
      },
    ];
  };

  const getCheckupsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'personId',
        headerName: translateText(
          'administration.healthAndSafety.columns.personId'
        ),
        width: 120,
        renderCell: (params) => params.row.personId,
      },
      {
        field: 'personType',
        headerName: translateText(
          'administration.healthAndSafety.columns.personType'
        ),
        width: 120,
        renderCell: (params) => params.row.personType,
      },
      {
        field: 'checkupType',
        headerName: translateText(
          'administration.healthAndSafety.columns.checkupType'
        ),
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.checkupType}
            color={
              params.row.checkupType === 'ANNUAL'
                ? 'primary'
                : params.row.checkupType === 'MEDICAL'
                  ? 'warning'
                  : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'checkupDate',
        headerName: translateText(
          'administration.healthAndSafety.columns.checkupDate'
        ),
        width: 130,
        renderCell: (params) => params.row.checkupDate?.toLocaleDateString(),
      },
      {
        field: 'doctorName',
        headerName: translateText(
          'administration.healthAndSafety.columns.doctor'
        ),
        width: 150,
        renderCell: (params) => params.row.doctorName,
      },
      {
        field: 'hospitalClinic',
        headerName: translateText(
          'administration.healthAndSafety.columns.location'
        ),
        width: 150,
        renderCell: (params) => params.row.hospitalClinic,
      },
      {
        field: 'isCleared',
        headerName: translateText(
          'administration.healthAndSafety.columns.status'
        ),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isCleared
                ? translateText('administration.healthAndSafety.status.cleared')
                : translateText(
                  'administration.healthAndSafety.status.notCleared'
                )
            }
            color={params.row.isCleared ? 'success' : 'error'}
            size="small"
          />
        ),
      },
      {
        field: 'nextCheckupDate',
        headerName: translateText(
          'administration.healthAndSafety.columns.nextCheckup'
        ),
        width: 130,
        renderCell: (params) =>
          params.row.nextCheckupDate?.toLocaleDateString(),
      },
    ];
  };

  const getGridColumns = () => {
    switch (viewType) {
      case 'incidents':
        return getIncidentsGridColumns();
      case 'protocols':
        return getProtocolsGridColumns();
      case 'contacts':
        return getContactsGridColumns();
      case 'checkups':
        return getCheckupsGridColumns();
      default:
        return getIncidentsGridColumns();
    }
  };

  const getGridRows = () => {
    switch (viewType) {
      case 'incidents':
        return incidents;
      case 'protocols':
        return protocols;
      case 'contacts':
        return emergencyContacts;
      case 'checkups':
        return healthCheckups;
      default:
        return incidents;
    }
  };

  const handleRowClick = (params: any) => {
    onRowClick(params.row);
  };

  const rows = getGridRows();
  const columns = getGridColumns();

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {translateText('administration.healthAndSafety.loading')}
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }} data-testid={`health-safety-grid-${viewType}`}>
      <Grid item xs={12}>
        <PaginatedGrid
          dataTestId={`health-safety-${viewType}-grid`}
          rows={rows}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={(params) => 'cursor-pointer'}
          hideColumnsOnMobile={['id', 'actions']}
        />
      </Grid>
      <Grid item xs={12} sx={{ my: { xs: 1, sm: 2 } }}>
        {viewType !== 'checkups' && <Divider />}
      </Grid>
    </Grid>
  );
};

export default HealthAndSafetyGrid;
